import { useRef, useState } from 'react';
import type React from 'react';

import { Avatar, Text } from '@change/ds-core-ui';
import { cn } from '@change/ds-utils';

import { Translate, useI18n } from '@change-corgi/core-react/i18n';

import type { PetitionDetailsPageData } from '#src/app/pages/petitionDetails/pageContext';
import { usePetitionStarterInfoTracking, usePetitionStarterPhoto } from '#src/app/pages/petitionDetails/shared/hooks';

type StarterPopoverProps = {
	photoUrl?: string | null;
	displayName: string;
	description?: string | null;
};

const STARTER_POPOVER_ID = 'petition-starter-popover';

type Petition = PetitionDetailsPageData['petition'];

type Props = {
	user: Petition['user'];
	organization: Petition['organization'];
	createdAt: Petition['createdAt'];
	createdAtLocalized: Petition['createdAtLocalized'];
	petitionPageStarterInHeaderEnabled: boolean;
};

type StarterClusterProps = {
	starterPhotoUrl?: string;
	displayName: string;
	hasDescription: boolean;
};

const StarterCluster: React.FC<StarterClusterProps> = ({ starterPhotoUrl, displayName, hasDescription }) => (
	<span className="flex min-w-0 items-center gap-2">
		{starterPhotoUrl && (
			<span className="shrink-0">
				<Avatar size="x-small" src={starterPhotoUrl} alt={displayName} />
			</span>
		)}
		<Text
			variant="body"
			size="small"
			fontWeight="bold"
			truncate
			underline={hasDescription}
			className={cn('text-typography-inverse-strong-fixed min-w-0', { 'cursor-pointer': hasDescription })}
		>
			{displayName}
		</Text>
	</span>
);

const StarterPopoverCard: React.FC<StarterPopoverProps> = ({ photoUrl, displayName, description }) => (
	<div
		id={STARTER_POPOVER_ID}
		role="tooltip"
		className="bg-typography-inverse-strong absolute bottom-full left-0 z-50 mb-3 w-max overflow-hidden rounded-lg shadow-xl"
	>
		<div className="flex items-center gap-3 p-4 pb-0">
			{photoUrl && <Avatar size="medium" src={photoUrl} alt={displayName} />}
			<Text variant="heading" size="x-small" fontWeight="bold" className="text-typography-strong">
				{displayName}
			</Text>
		</div>
		{description && (
			<Text as="p" variant="body" className="text-typography-weak max-w-xs p-4">
				{description}
			</Text>
		)}
	</div>
);

// oxlint-disable-next-line complexity
export const PetitionStarterInHeader: React.FC<Props> = ({
	user,
	organization,
	createdAt,
	createdAtLocalized,
	petitionPageStarterInHeaderEnabled,
}) => {
	const { photoUrl: starterPhotoUrl } = usePetitionStarterPhoto();
	const [showCard, setShowCard] = useState(false);
	const { trackStarterInfoClick } = usePetitionStarterInfoTracking();
	const hasTracked = useRef(false);
	const { localizeDate } = useI18n();

	if (!petitionPageStarterInHeaderEnabled) return null;
	if (organization) return null;
	if (user.anonymous) return null;

	const createdAtLabel = createdAtLocalized || (createdAt ? localizeDate(createdAt, 'date', 'long') : undefined);

	const showPopOver = () => {
		setShowCard(true);
		if (user.description && !hasTracked.current) {
			hasTracked.current = true;
			trackStarterInfoClick();
		}
	};

	return (
		<div className="flex items-center gap-2 pt-2" data-qa="petition-starter-in-header">
			<div
				className="relative flex items-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
				tabIndex={user.description ? 0 : -1}
				aria-describedby={showCard && user.description ? STARTER_POPOVER_ID : undefined}
				onMouseEnter={showPopOver}
				onMouseLeave={() => setShowCard(false)}
				onFocus={showPopOver}
				onBlur={() => setShowCard(false)}
			>
				<StarterCluster
					starterPhotoUrl={starterPhotoUrl}
					displayName={user.displayName}
					hasDescription={!!user.description}
				/>
				{showCard && user.description && (
					<StarterPopoverCard
						photoUrl={starterPhotoUrl}
						displayName={user.displayName}
						description={user.description}
					/>
				)}
			</div>
			{(createdAtLocalized || createdAt) && (
				<>
					<span className="bg-typography-inverse-strong-fixed/60 h-3 w-px" aria-hidden />
					<Text variant="body" size="small" className="text-typography-inverse-strong-fixed/60">
						<Translate
							value="corgi.petition_gamma.petition_starter_in_header.created"
							replacements={{ date: createdAtLabel }}
						/>
					</Text>
				</>
			)}
		</div>
	);
};
